import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const registrationToken = searchParams.get("token");
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [tokenData, setTokenData] = useState(null);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  useEffect(() => {
    const init = async () => {
      // If there's a registration token, validate it
      if (registrationToken) {
        try {
          const response = await axios.get(
            `${API}/auth/verify-registration-token/${registrationToken}`
          );
          setTokenValid(true);
          setTokenData(response.data);
          setFormData((prev) => ({
            ...prev,
            name: response.data.customer_name || "",
            email: response.data.email || "",
          }));
        } catch (error) {
          setTokenValid(false);
        }
        setIsLoading(false);
        return;
      }

      // Check if already authenticated
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
        });
        if (response.data) {
          const role = response.data.role;
          if (role === "customer") {
            navigate("/customer-dashboard", { replace: true });
          } else {
            navigate("/dashboard", { replace: true });
          }
        }
      } catch (error) {
        // Not authenticated — silently proceed to show register page
        console.log("Auth check: not authenticated, showing register");
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [navigate, registrationToken]);

  const validatePassword = (pwd) => {
    if (pwd.length < 10) return "Password must be at least 10 characters";
    if (!/[a-z]/.test(pwd)) return "Password must contain a lowercase letter";
    if (!/[A-Z]/.test(pwd)) return "Password must contain an uppercase letter";
    if (!/\d/.test(pwd)) return "Password must contain a digit";
    return null;
  };

  const handleCustomerRegistration = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    const pwdError = validatePassword(formData.password);
    if (pwdError) {
      toast.error(pwdError);
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/auth/register-customer`, null, {
        params: {
          token: registrationToken,
          password: formData.password,
        },
      });

      setRegistrationComplete(true);
      toast.success("Account created successfully!");
    } catch (error) {
      const message = error.response?.data?.detail || "Registration failed";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegularRegistration = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    const pwdError = validatePassword(formData.password);
    if (pwdError) {
      toast.error(pwdError);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(
        `${API}/auth/register`,
        {
          name: formData.name,
          email: formData.email,
          password: formData.password,
        },
        {
          withCredentials: true,
        }
      );

      if (response.data.user) {
        toast.success("Account created successfully");
        navigate("/dashboard", { state: { user: response.data.user }, replace: true });
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Registration failed";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  // Customer registration with token - success state
  if (registrationToken && registrationComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="w-full max-w-md rounded-sm">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-semibold text-slate-900 mb-2">
              Account Created!
            </h2>
            <p className="text-slate-500 mb-6">
              Your customer account has been created successfully. You can now sign
              in to manage your installations and book services.
            </p>
            <Button
              onClick={() => navigate("/login")}
              className="w-full rounded-sm bg-slate-900 hover:bg-slate-800"
              data-testid="go-to-login-btn"
            >
              Sign In to Your Account
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Customer registration with token - invalid token
  if (registrationToken && tokenValid === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="w-full max-w-md rounded-sm">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-semibold text-slate-900 mb-2">
              Invalid or Expired Link
            </h2>
            <p className="text-slate-500 mb-6">
              This registration link is invalid or has expired. Please contact the
              administrator to request a new registration link.
            </p>
            <Button
              onClick={() => navigate("/login")}
              variant="outline"
              className="w-full rounded-sm"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Customer registration with token - form
  if (registrationToken && tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="w-full max-w-md rounded-sm">
          <CardHeader className="text-center">
            <div className="w-12 h-12 bg-slate-900 rounded-sm flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-7 h-7 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            </div>
            <CardTitle className="text-2xl">Create Your Account</CardTitle>
            <p className="text-slate-500 mt-2">
              Welcome! Set up your password to access your customer portal.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCustomerRegistration} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  disabled
                  className="rounded-sm h-11 bg-slate-50"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  disabled
                  className="rounded-sm h-11 bg-slate-50"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Create a password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  required
                  minLength={6}
                  className="rounded-sm h-11"
                  data-testid="password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={(e) =>
                    setFormData({ ...formData, confirmPassword: e.target.value })
                  }
                  required
                  className="rounded-sm h-11"
                  data-testid="confirm-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white rounded-sm font-medium"
                data-testid="create-account-btn"
              >
                {isSubmitting ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Creating account...
                  </div>
                ) : (
                  "Create Account"
                )}
              </Button>
            </form>

            <p className="mt-6 text-center text-sm text-slate-500">
              Already have an account?{" "}
              <Link to="/login" className="text-slate-900 font-medium hover:underline">
                Sign in
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Regular registration (non-customer)
  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Left side - Background image */}
      <div
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1767294274634-613a3545e36d?crop=entropy&cs=srgb&fm=jpg&q=85')`,
        }}
      >
        <div className="absolute inset-0 bg-slate-900/60"></div>
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h1 className="text-4xl font-semibold tracking-tight mb-4">TaskForge</h1>
          <p className="text-lg text-slate-200 max-w-md">
            Manufacturing project management built for teams that build things.
            Track projects, suppliers, materials, and operations in one place.
          </p>
        </div>
      </div>

      {/* Right side - Register form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-sm animate-slide-up">
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-8">
              <div className="w-8 h-8 bg-slate-900 rounded-sm flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
              </div>
              <span className="text-xl font-semibold text-slate-900 lg:hidden">
                TaskForge
              </span>
            </div>
            <h2 className="text-2xl font-semibold text-slate-900 tracking-tight">
              Create an account
            </h2>
            <p className="text-slate-500 mt-2">Get started with your workspace</p>
          </div>

          <form onSubmit={handleRegularRegistration} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="rounded-sm h-11"
                data-testid="name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="rounded-sm h-11"
                data-testid="email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Create a password"
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                required
                minLength={6}
                className="rounded-sm h-11"
                data-testid="password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirm your password"
                value={formData.confirmPassword}
                onChange={(e) =>
                  setFormData({ ...formData, confirmPassword: e.target.value })
                }
                required
                className="rounded-sm h-11"
                data-testid="confirm-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white rounded-sm font-medium transition-all duration-200"
              data-testid="register-btn"
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Creating account...
                </div>
              ) : (
                "Create account"
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-500">
            Already have an account?{" "}
            <Link to="/login" className="text-slate-900 font-medium hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
